import { createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { $app, AppState } from '@nx-frontend/shared/store/app';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $user } from '@nx-frontend/shared/store/user';
import { Org, User } from '@nx-frontend/shared/types';
import { Workspace } from '@nx-frontend/shared/types';

export interface OrgChatState {
  fetchingInitialization: boolean;
  fetchingUnreads: boolean;
  initilized: boolean | null;
  error: boolean | null;
  unreadMessagesCount: number;
}

export const initialOrgChatState = {
  fetchingInitialization: false,
  fetchingUnreads: false,
  initilized: null,
  error: null,
  unreadMessagesCount: 0,
};

export const $orgChat = createStore<OrgChatState>(initialOrgChatState);

export const checkChatInitializationEvent = createEvent();
export const startFetchChatInitializationEvent = createEvent();
export const startFetchUnreadMessagesEvent = createEvent();

export const fetchChatInitializationFx = createEffect(
  async ({
    currentOrganization,
  }: {
    currentOrganization: Org;
  }): Promise<{ state: boolean | null; error: boolean | null }> => {
    let state: boolean | null = null;
    let error: boolean | null = null;

    try {
      const result = await axiosClient.get<{ state: boolean }>(
        `${generalEnv.chatApiUrl}/orgs/initialized/${currentOrganization.globalId}`
      );
      state = result.data.state;
    } catch (err) {
      error = true;
      console.error('[CHAT] check chat initialized for organization failure', err);
    }

    return { state, error };
  }
);

export const fetchUnreadMessagesFx = createEffect(
  async ({
    currentOrganization,
    currentWorkspace,
    user,
  }: {
    currentOrganization: Org;
    currentWorkspace: Workspace;
    user: User;
  }): Promise<number> => {
    try {
      const result = await axiosClient.get<{ unreadsCount: number }>(
        `${generalEnv.chatApiUrl}/me/unreads-tmp?orgId=${currentOrganization.globalId}&scope=${currentWorkspace.globalId}&userId=${user.id}`
      );
      return result.data.unreadsCount;
    } catch (err) {
      console.error('[CHAT] fetch unread messages count failure', err);
      return 0;
    }
  }
);

export const initializeChatFx = createEffect(
  async ({
    currentOrganization,
    currentWorkspace,
    app,
  }: {
    currentOrganization: Org;
    currentWorkspace: Workspace;
    app: AppState;
  }): Promise<{ jobId: string | null; token: string; error: boolean | null }> => {
    const orgId = currentOrganization.globalId;
    const workspaceId = currentWorkspace.globalId;
    const sessionId = app.sessionId;

    let jobId: string | null = null;
    let error: boolean | null = null;
    let token = '';
    try {
      const tokenResult = await axiosClient.post(
        `${generalEnv.chatApiUrl}/token?orgId=${orgId}&scopes=${workspaceId}`,
        {
          sessionId,
        }
      );
      token = tokenResult?.data?.token;

      if (token) {
        const orgInitResult = await axiosClient.post(
          `${generalEnv.chatApiUrl}/orgs/init`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        jobId = orgInitResult?.data?.uuid;
      }
    } catch (err) {
      error = true;
      console.log('[CHAT] token', err);
    }

    return { jobId, token, error };
  }
);

enum JOB_STATUS {
  PENDING = 'pending',
  RUNNING = 'running',
  FAILURE = 'failure',
  SKIP = 'skip',
  DONE = 'done',
}

export const startListenInitializationFx = createEffect(
  async ({
    user,
    token,
    jobId,
  }: {
    user: User;
    token: string;
    jobId: string | null;
  }): Promise<{ initilized: boolean | null; error: boolean | null }> => {
    let IID: NodeJS.Timer | number = 0;
    const jobUrl = `${generalEnv.chatApiUrl}/jobs/${jobId}?jobType=createOrg&initiatorType=user&initiatorId=${user.id}`;

    return await new Promise<{ initilized: boolean | null; error: boolean | null }>((resolve) => {
      IID = setInterval(async () => {
        try {
          const jobResult = await axiosClient.get(jobUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const jobStatus = jobResult?.data?.status;
          if (jobStatus) {
            switch (jobStatus) {
              case JOB_STATUS.PENDING || JOB_STATUS.RUNNING: {
                break;
              }
              case JOB_STATUS.DONE: {
                resolve({ initilized: true, error: null });
                clearInterval(IID);
                break;
              }
              case JOB_STATUS.FAILURE: {
                resolve({ initilized: false, error: true });
                clearInterval(IID);
                break;
              }
            }
          }
        } catch (err) {
          resolve({ initilized: false, error: true });
          clearInterval(IID);
        }
      }, 10000);
    });
  }
);

export const stopListenInitializationEvent = createEvent<{ jobId: string; token: string }>();

/**
 * Check chat initialization status
 */
sample({
  source: {
    orgChat: $orgChat,
    currentOrganization: $currentOrganization,
  },
  clock: checkChatInitializationEvent,
  fn: ({ orgChat, currentOrganization }, params) => ({ orgChat, currentOrganization, params }),
  filter: ({ orgChat }) => {
    return orgChat.fetchingInitialization === false;
  },
  target: [fetchChatInitializationFx, startFetchChatInitializationEvent],
});

$orgChat.on(startFetchChatInitializationEvent, (state) => ({ ...state, fetchingInitialization: true }));

$orgChat.on(fetchChatInitializationFx.doneData, (_, { state, error }) => {
  return {
    ..._,
    fetchingInitialization: false,
    initilized: state,
    error,
  };
});

/**
 * In case if chat is not initialized, initialize it
 */
sample({
  source: {
    user: $user,
    app: $app,
    currentOrganization: $currentOrganization,
    currentWorkspace: $currentWorkspace,
  },
  clock: fetchChatInitializationFx.doneData,
  fn: (_) => _,
  filter: (_, { state }) => {
    return state === false;
  },
  target: initializeChatFx,
});

$orgChat.on(initializeChatFx.doneData, (_, { error }) => {
  return {
    ..._,
    error,
  };
});

/**
 * After create initialization job, start listen it
 */
sample({
  source: {
    user: $user,
  },
  clock: initializeChatFx.doneData,
  fn: ({ user }, { jobId, token }) => ({ user, jobId, token }),
  target: startListenInitializationFx,
});

$orgChat.on(startListenInitializationFx.doneData, (_, { initilized, error }) => {
  return {
    ..._,
    initilized,
    error,
  };
});

sample({
  source: {
    orgChat: $orgChat,
    currentOrganization: $currentOrganization,
    currentWorkspace: $currentWorkspace,
    user: $user,
  },
  clock: startFetchUnreadMessagesEvent,
  fn: ({ orgChat, currentOrganization, currentWorkspace, user }) => ({
    orgChat,
    currentOrganization,
    currentWorkspace,
    user,
  }),
  target: fetchUnreadMessagesFx,
});

$orgChat.on(startFetchUnreadMessagesEvent, (state) => ({ ...state, fetchingUnreads: true }));

$orgChat.on(fetchUnreadMessagesFx.doneData, (_, unreadMessagesCount) => {
  return {
    ..._,
    fetchingUnreads: false,
    unreadMessagesCount,
  };
});
